import { each, includes, isEmpty, map, merge, reduce } from "lodash";
import { Facet } from "@linkfluence/models/facet";
import { Clause } from "@radarly/models/clause";
import { createClauseFromInnerClause } from "@radarly/models/clause/creator";
import { mergeClauses } from "@radarly/models/clause/merge";
import { getClauseFromWizardAccounts } from "./account";
import { WizardTemplate, WizardTemplateSection } from "./model";
import {
  AccountsConfigurationField,
  WizardConfiguration,
} from "./model/configuration";
import { CardsSection, LoopCardsSection } from "./model/section";
import { getMandatoryFieldsFromSections } from "./section";

export function getConfigurationFieldsFromWizardTemplate(
  template: WizardTemplate
) {
  return getMandatoryFieldsFromSections(template.sections);
}

export function hasValidConfigurationTemplate(
  template: WizardTemplate,
  configuration: WizardConfiguration
) {
  if (isEmpty(configuration.title)) {
    return false;
  }
  const fields = getConfigurationFieldsFromWizardTemplate(template);
  if (includes(fields, "query") && isEmpty(configuration.fields.query)) {
    return false;
  }
  if (includes(fields, "queries") && isEmpty(configuration.fields.queries)) {
    return false;
  }
  return true;
}

export function isAccountFieldEmpty(field: AccountsConfigurationField) {
  return (
    field.me === false && isEmpty(field.accounts) && isEmpty(field.corpora)
  );
}

const getSectionValidity = (
  sectionFields: string[],
  fields: WizardConfiguration["fields"]
) => {
  let isValid = true;
  if (includes(sectionFields, "query") && isEmpty(fields.query)) {
    isValid = false;
  }
  if (includes(sectionFields, "queries") && isEmpty(fields.queries)) {
    isValid = false;
  }
  if (includes(sectionFields, "competitors") && isEmpty(fields.competitors)) {
    isValid = false;
  }
  each(["owned", "influencers"], (field) => {
    if (includes(sectionFields, field) && isAccountFieldEmpty(fields[field])) {
      isValid = false;
    }
  });

  return isValid;
};

export function hasValidConfigurationSection(
  section: WizardTemplateSection,
  fields: WizardConfiguration["fields"]
) {
  const sectionFields = getMandatoryFieldsFromSections([section]);
  return getSectionValidity(sectionFields, fields);
}

export function hasMatchingWithoutConditionSection(
  section: CardsSection | LoopCardsSection,
  fields: WizardConfiguration["fields"]
) {
  const withoutFields =
    (section.conditions && section.conditions.without) || [];
  return withoutFields.length > 0 && getSectionValidity(withoutFields, fields);
}

interface WizardConfigurationClauseEntryTransformer {
  getClause(fields: WizardConfiguration["fields"]): Clause;
  getWithoutClause(fields: WizardConfiguration["fields"]): Clause;
  getField(): keyof WizardConfiguration["fields"];
}

interface WizardConfigurationClauseEntryCombinator {
  getClause(
    fields: WizardConfiguration["fields"],
    wizardClauses?: Partial<Record<keyof WizardConfiguration["fields"], Clause>>
  ): Clause;
  getField(): keyof WizardConfiguration["fields"];
}

class WizardConfigurationClauseQueryTransformer
  implements WizardConfigurationClauseEntryTransformer
{
  getField() {
    return "query" as const;
  }
  getClause(fields: WizardConfiguration["fields"]) {
    if (isEmpty(fields.query)) {
      return null;
    }
    return createClauseFromInnerClause({
      [Facet.Focuses]: [fields.query.id],
    });
  }

  getWithoutClause(fields: WizardConfiguration["fields"]) {
    if (isEmpty(fields.query)) {
      return null;
    }
    return createClauseFromInnerClause({
      not: {
        [Facet.Focuses]: [fields.query.id],
      },
    });
  }
}

class WizardConfigurationClauseQueriesTransformer
  implements WizardConfigurationClauseEntryTransformer
{
  getField() {
    return "queries" as const;
  }
  getClause(fields: WizardConfiguration["fields"]) {
    if (isEmpty(fields.queries)) {
      return null;
    }
    return createClauseFromInnerClause({
      [Facet.Focuses]: map(fields.queries, "id"),
    });
  }

  getWithoutClause(fields: WizardConfiguration["fields"]) {
    if (isEmpty(fields.queries)) {
      return null;
    }
    return createClauseFromInnerClause({
      not: {
        [Facet.Focuses]: map(fields.queries, "id"),
      },
    });
  }
}

class WizardConfigurationClauseCompetitorsTransformer
  implements WizardConfigurationClauseEntryTransformer
{
  getField() {
    return "competitors" as const;
  }
  getClause(fields: WizardConfiguration["fields"]) {
    if (isEmpty(fields.competitors)) {
      return null;
    }
    return createClauseFromInnerClause({
      [Facet.Focuses]: map(fields.competitors, "id"),
    });
  }

  getWithoutClause(fields: WizardConfiguration["fields"]) {
    if (isEmpty(fields.competitors)) {
      return null;
    }
    return createClauseFromInnerClause({
      not: {
        [Facet.Focuses]: map(fields.competitors, "id"),
      },
    });
  }
}

type AccountConfigurationField = keyof Pick<
  WizardConfiguration["fields"],
  "owned" | "influencers"
>;

abstract class WizardConfigurationClauseAccountsTransformer<
  F extends AccountConfigurationField
> implements WizardConfigurationClauseEntryTransformer
{
  abstract getField(): F;

  public getClause(fields: WizardConfiguration["fields"]) {
    return this.buildClause(fields);
  }
  private buildClause(fields: WizardConfiguration["fields"], without = false) {
    const field = this.getField();
    const fieldData = fields[field];
    if (isEmpty(fieldData)) {
      return null;
    }
    return mergeClauses(
      mergeClauses(
        this.getClauseFromWholeCorpora(fieldData, without),
        this.getClauseFromMeFilter(fieldData, without)
      ),
      this.getClauseFromAccounts(fieldData, without)
    );
  }

  public getWithoutClause(fields: WizardConfiguration["fields"]) {
    return this.buildClause(fields, true);
  }

  private getClauseFromMeFilter(
    ownedField: WizardConfiguration["fields"][F],
    without: boolean
  ) {
    if (!ownedField?.me) {
      return null;
    }
    return createClauseFromInnerClause({
      author: without ? "other" : "me",
    });
  }
  private getClauseFromAccounts(
    ownedField: WizardConfiguration["fields"][F],
    without: boolean
  ) {
    if (isEmpty(ownedField?.accounts)) {
      return null;
    }
    return getClauseFromWizardAccounts(ownedField?.accounts, without);
  }
  private getClauseFromWholeCorpora(
    ownedField: WizardConfiguration["fields"][F],
    without: boolean
  ) {
    if (isEmpty(ownedField?.corpora)) {
      return null;
    }
    const clauseValue = {
      [Facet.Corpus]: map(ownedField?.corpora, "id"),
    };
    return createClauseFromInnerClause(
      without ? { not: clauseValue } : clauseValue
    );
  }
}

class WizardConfigurationClauseOwnedMediaTransformer extends WizardConfigurationClauseAccountsTransformer<"owned"> {
  getField() {
    return "owned" as const;
  }
}

class WizardConfigurationClauseInfluencersTransformer extends WizardConfigurationClauseAccountsTransformer<"influencers"> {
  getField() {
    return "influencers" as const;
  }
}

class WizardConfigurationClauseEarnedMediaCombinator
  implements WizardConfigurationClauseEntryCombinator
{
  getField() {
    return "earned" as const;
  }
  getClause(
    fields: WizardConfiguration["fields"],
    wizardClauses: Partial<Record<keyof WizardConfiguration["fields"], Clause>>
  ) {
    const field = this.getField();
    const fieldData = fields[field];
    if (isEmpty(wizardClauses["owned"])) {
      return null;
    }

    if (!!fieldData) {
      return wizardClauses["without_owned"];
    } else {
      return mergeClauses(
        wizardClauses["without_owned"],
        wizardClauses["without_influencers"]
      );
    }
  }
}

class WizardConfigurationClauseTransformer {
  private transformers: WizardConfigurationClauseEntryTransformer[] = [];
  public constructor(
    transformers: WizardConfigurationClauseEntryTransformer[]
  ) {
    this.transformers = transformers;
  }
  public getClausesFromFields(fields: WizardConfiguration["fields"]) {
    const clauses: Partial<
      Record<keyof WizardConfiguration["fields"], Clause>
    > = {};
    return reduce(
      this.transformers,
      (acc, transformer) => {
        const clause = transformer.getClause(fields);
        const withoutClause = transformer.getWithoutClause(fields);
        if (!isEmpty(clause)) {
          acc[transformer.getField()] = clause;
        }
        if (!isEmpty(withoutClause)) {
          acc["without_" + transformer.getField()] = withoutClause;
        }
        return acc;
      },
      clauses
    );
  }
}

export class WizardConfigurationClauseTransformerFactory {
  static getInstance() {
    return new WizardConfigurationClauseTransformer([
      new WizardConfigurationClauseQueryTransformer(),
      new WizardConfigurationClauseQueriesTransformer(),
      new WizardConfigurationClauseCompetitorsTransformer(),
      new WizardConfigurationClauseOwnedMediaTransformer(),
      new WizardConfigurationClauseInfluencersTransformer(),
    ]);
  }
}

class WizardConfigurationClauseCombinator {
  private combinators: WizardConfigurationClauseEntryCombinator[] = [];
  public constructor(combinators: WizardConfigurationClauseEntryCombinator[]) {
    this.combinators = combinators;
  }
  public getClausesFromFields(
    fields: WizardConfiguration["fields"],
    wizardClauses?: Partial<Record<keyof WizardConfiguration["fields"], Clause>>
  ) {
    const clauses: Partial<
      Record<keyof WizardConfiguration["fields"], Clause>
    > = {};
    return reduce(
      this.combinators,
      (acc, transformer) => {
        const clause = transformer.getClause(fields, wizardClauses);
        if (!isEmpty(clause)) {
          acc[transformer.getField()] = clause;
        }
        return acc;
      },
      clauses
    );
  }
}

export class WizardConfigurationClauseCombinatorFactory {
  static getInstance() {
    return new WizardConfigurationClauseCombinator([
      new WizardConfigurationClauseEarnedMediaCombinator(),
    ]);
  }
}

export function getWizardConfigurationClauseGenerator(fields) {
  const clauses =
    WizardConfigurationClauseTransformerFactory.getInstance().getClausesFromFields(
      fields
    );
  WizardConfigurationClauseCombinatorFactory.getInstance().getClausesFromFields(
    fields,
    clauses
  );
  return merge(
    clauses,
    WizardConfigurationClauseCombinatorFactory.getInstance().getClausesFromFields(
      fields,
      clauses
    )
  );
}
